import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Share2, Copy, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useConversationShare, buildShareUrl } from '@/hooks/useConversationShare';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  conversationId: string | null;
}

export function ShareConversationModal({ open, onOpenChange, conversationId }: Props) {
  const { share, isLoading, createShare, refreshSnapshot, revokeShare } = useConversationShare(conversationId);
  const [copied, setCopied] = useState(false);

  const url = share ? buildShareUrl(share.share_token) : '';

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreate = async () => {
    try {
      await createShare.mutateAsync();
      toast.success('Link de compartilhamento criado');
    } catch (e) {
      toast.error('Erro ao criar link', { description: (e as Error).message });
    }
  };

  const handleRefresh = async () => {
    if (!share) return;
    try {
      await refreshSnapshot.mutateAsync(share.id);
      toast.success('Snapshot atualizado');
    } catch (e) {
      toast.error('Erro ao atualizar snapshot', { description: (e as Error).message });
    }
  };

  const handleRevoke = async () => {
    if (!share) return;
    try {
      await revokeShare.mutateAsync(share.id);
      toast.success('Compartilhamento encerrado');
    } catch (e) {
      toast.error('Erro ao revogar', { description: (e as Error).message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Compartilhar conversa
          </DialogTitle>
          <DialogDescription>
            Apenas membros da plataforma poderão abrir o link. Quem receber poderá ler a conversa e continuá-la em uma derivação própria.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-tertiary" />
          </div>
        ) : !share ? (
          <div className="space-y-4">
            <p className="text-sm text-secondary">
              Um snapshot da conversa atual será criado. Mensagens novas não aparecerão no link até você atualizar o snapshot.
            </p>
            <button
              onClick={handleCreate}
              disabled={createShare.isPending}
              className="w-full px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {createShare.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Criar link de compartilhamento
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={url}
                className="flex-1 h-9 px-3 rounded-md border border-color bg-surface text-xs text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className="px-3 h-9 brand-primary rounded-md text-xs font-medium flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            <p className="text-xs text-tertiary">
              Snapshot atual: {new Date(share.snapshot_at).toLocaleString('pt-BR')}
            </p>
            <div className="flex items-center gap-2 pt-2 border-t border-color">
              <button
                onClick={handleRefresh}
                disabled={refreshSnapshot.isPending}
                className="flex-1 px-3 py-2 border border-color rounded-md text-xs text-primary hover:bg-hover flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {refreshSnapshot.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Atualizar snapshot
              </button>
              <button
                onClick={handleRevoke}
                disabled={revokeShare.isPending}
                className="flex-1 px-3 py-2 border border-color rounded-md text-xs text-destructive hover:bg-hover flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {revokeShare.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Parar de compartilhar
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
